import { useMemo } from 'react';

import { useAccountFeatures } from '@dbt-labs/app-nav';

import { FUSION_DBT_VERSIONS } from './constants';
import {
  FusionEligibility,
  useEnvironment,
  useFusionStatus,
  useJobsWithSAOEnablement,
} from './requests';
import { useDeprecationWarningForProject } from './requests';
import { FeatureFlags } from './types';

export type UseFusionSaoValidationsArgs = {
  accountId: number;
  environmentId: number;
  featureFlags: FeatureFlags;
  /**
   * Optional. If omitted, derived from the environment response (requires `includeRelated: ['project']`).
   */
  projectId?: number;
  /**
   * Optional. If true, pauses all data fetching queries.
   */
  pause?: boolean;
};

export type FusionSaoValidations = {
  fusionEligibility: FusionEligibility | undefined;
  fusionLastDetectedAt: string | undefined;
  isFusionEligibleForProject: boolean;
  canEnableFusion: boolean;
  isUsingFusion: boolean;
  isSaoEligible: boolean;
  isSaoEnabledForProject: boolean;
  totalJobs: number;
  jobsWithSAOEnabled: number;
  isLoading: boolean;
};

/**
 * Hook to compute the validation state for the Fusion and SAO features.
 * @param accountId - The account ID
 * @param environmentId - The environment ID
 * @param projectId - The project ID
 * @param featureFlags - The feature flags
 * @returns The validation state for the Fusion and SAO features.
 * - fusionEligibility: The eligibility of the Fusion feature.
 * - fusionLastDetectedAt: The last detected at time of the Fusion feature.
 * - isFusionEnabledForProject: Whether the project is enabled for Fusion.
 * - isFusionEligibleForProject: Whether the project is eligible for Fusion (requires both fusionStatus.is_eligible === true and deprecation warning readiness: Ready/ReadyNonconformant enum, 404 status, or legacy case of null eligibility + last_detected_at).
 * - canEnableFusion: Whether the user can enable Fusion.
 * - isUsingFusion: Whether the user is using Fusion.
 * - isSaoEligible: Whether the account is eligible for SAO (account feature or feature flag).
 * - isSaoEnabledForProject: Whether at least one job in the project has SAO enabled.
 * - totalJobs: Total number of jobs in the project.
 * - jobsWithSAOEnabled: Number of jobs with SAO enabled.
 * - isLoading: Whether the validation state is loading.
 */
export const useFusionSaoValidations = ({
  accountId,
  environmentId,
  projectId,
  featureFlags,
  pause,
}: UseFusionSaoValidationsArgs): FusionSaoValidations => {
  const { orc2609ShowFusionToggle, orc2664SaoBeta } = featureFlags;

  // Conditionally call hook based on pause - pass undefined accountId when paused
  const validAccountId = pause ? undefined : accountId;
  const accountFeaturesQuery = useAccountFeatures(validAccountId);
  const environmentQuery = useEnvironment({
    environmentId,
    accountId: validAccountId,
    includeRelated: ['project'],
    preventUpdate: true,
    pause,
  });

  const environment = environmentQuery.data;

  const resolvedProjectId = projectId ?? environment?.project?.id;

  const { data: fusionStatus, isLoading: isFusionStatusLoading } = useFusionStatus(
    accountId,
    resolvedProjectId,
    pause,
  );

  const deprecationWarningQuery = useDeprecationWarningForProject(
    accountId,
    resolvedProjectId,
    pause,
  );

  const { data: jobsSaoEnablement, isLoading: isJobsSaoLoading } =
    useJobsWithSAOEnablement(accountId, resolvedProjectId, pause);

  const isUsingFusion = useMemo(() => {
    return FUSION_DBT_VERSIONS.has(environment?.dbt_version ?? '');
  }, [environment?.dbt_version]);

  const isSaoEligible =
    !!accountFeaturesQuery.data?.stateAwareOrchestration || !!orc2664SaoBeta;

  const isSaoEnabledForProject = (jobsSaoEnablement?.jobsWithSAOEnabled ?? 0) > 0;
  const totalJobs = jobsSaoEnablement?.totalJobs ?? 0;
  const jobsWithSAOEnabled = jobsSaoEnablement?.jobsWithSAOEnabled ?? 0;

  const canEnableFusion = !!orc2609ShowFusionToggle && !!fusionStatus?.is_eligible;

  const isLoading =
    accountFeaturesQuery.isLoading ||
    environmentQuery.isLoading ||
    deprecationWarningQuery.isLoading ||
    isFusionStatusLoading ||
    isJobsSaoLoading;

  const fusionEligibility = useMemo(() => {
    return deprecationWarningQuery.failureReason?.status === 404
      ? FusionEligibility.Ready
      : deprecationWarningQuery.data?.fusion_eligibility;
  }, [
    deprecationWarningQuery.data?.fusion_eligibility,
    deprecationWarningQuery.failureReason?.status,
  ]);

  const isFusionEligibleForProject = useMemo(() => {
    // Both conditions must be true for Fusion to be eligible:
    // 1. fusionStatus.is_eligible === true (allowed to see/onboard)
    // 2. Project readiness check:
    //    - 404 status (all projects enabled)
    //    - OR fusion_eligibility == null && last_detected_at exists (legacy enabled state)
    //    - OR fusion_eligibility === Ready/ReadyNonconformant (new eligibility states)

    if (fusionStatus?.is_eligible !== true) {
      return false;
    }

    // Check deprecation warning for project readiness
    // 404 means all projects are fusion enabled (ready)
    if (deprecationWarningQuery.failureReason?.status === 404) {
      return true;
    }

    // Legacy case: fusion_eligibility == null && last_detected_at exists means Fusion is already enabled
    if (
      deprecationWarningQuery.data?.fusion_eligibility == null &&
      !!deprecationWarningQuery.data?.last_detected_at
    ) {
      return true;
    }

    // Check fusion_eligibility enum values
    const eligibility = deprecationWarningQuery.data?.fusion_eligibility;
    return (
      eligibility === FusionEligibility.Ready ||
      eligibility === FusionEligibility.ReadyNonconformant
    );
  }, [
    fusionStatus?.is_eligible,
    deprecationWarningQuery.data?.fusion_eligibility,
    deprecationWarningQuery.data?.last_detected_at,
    deprecationWarningQuery.failureReason?.status,
  ]);

  return useMemo(
    () => ({
      fusionEligibility,
      fusionLastDetectedAt: deprecationWarningQuery.data?.last_detected_at,
      isFusionEligibleForProject,
      canEnableFusion,
      isUsingFusion,
      isSaoEligible,
      isSaoEnabledForProject,
      totalJobs,
      jobsWithSAOEnabled,
      isLoading,
    }),
    [
      fusionEligibility,
      deprecationWarningQuery.data?.last_detected_at,
      isFusionEligibleForProject,
      canEnableFusion,
      isUsingFusion,
      isSaoEligible,
      isSaoEnabledForProject,
      totalJobs,
      jobsWithSAOEnabled,
      isLoading,
    ],
  );
};
